import React from "react";
import { toast } from "sonner";
import { X, Send, MessageSquareShare, Twitter, Facebook, Linkedin, Copy, Mail, Phone } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// A social-share modal that works for any kind — Lab experiments, Foundation
// donations, or plain text/url. Pass `kind` + `id` (backend generates a card)
// OR pass `text` + `url` directly and skip the backend card.
export default function ShareModal({ open, onClose, kind, id, title, text, url }) {
  const { t } = useI18n();
  const [links, setLinks] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    api.post("/share/links", { kind, id, text: text || title, url })
       .then((r) => setLinks(r.data))
       .catch(() => setLinks(null));
  }, [open, kind, id, text, url, title]);

  if (!open) return null;

  const cardUrl = kind && id ? `${API}/share/card/${kind}/${id}.png` : null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(links?.url || url || window.location.href);
      toast.success(t("share.copy_link", "Link copied"));
    } catch { toast.error("Copy failed"); }
  };

  const targets = [
    { key: "whatsapp", label: "WhatsApp",  icon: MessageSquareShare, cls: "bg-emerald-600 hover:bg-emerald-700" },
    { key: "telegram", label: "Telegram",  icon: Send,               cls: "bg-sky-500 hover:bg-sky-600" },
    { key: "twitter",  label: "X",         icon: Twitter,            cls: "bg-stone-900 hover:bg-stone-800" },
    { key: "facebook", label: "Facebook",  icon: Facebook,           cls: "bg-blue-700 hover:bg-blue-800" },
    { key: "linkedin", label: "LinkedIn",  icon: Linkedin,           cls: "bg-blue-800 hover:bg-blue-900" },
    { key: "sms",      label: "SMS",       icon: Phone,              cls: "bg-stone-700 hover:bg-stone-800" },
    { key: "email",    label: "Email",     icon: Mail,               cls: "bg-orange-600 hover:bg-orange-700" },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="share-modal">
      <div className="w-full max-w-md rounded-3xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">{t("share.share_to", "Share to")}</div>
            <h3 className="heading text-xl font-black text-stone-900">{title || "Share"}</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800" data-testid="share-close"><X size={18} /></button>
        </div>

        {cardUrl && (
          <img
            src={cardUrl}
            alt="Share card preview"
            className="w-full rounded-2xl border border-stone-200 mb-3"
            data-testid="share-card-preview"
          />
        )}

        <div className="grid grid-cols-4 gap-2">
          {targets.map((t) => (
            <a
              key={t.key}
              href={links?.[t.key] || "#"}
              target={t.key === "email" || t.key === "sms" ? "_self" : "_blank"}
              rel="noopener noreferrer"
              className={`rounded-xl ${t.cls} text-white p-3 flex flex-col items-center gap-1`}
              data-testid={`share-target-${t.key}`}
            >
              <t.icon size={20} />
              <span className="text-[11px] font-black">{t.label}</span>
            </a>
          ))}
          <button
            onClick={copyLink}
            className="rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-800 p-3 flex flex-col items-center gap-1"
            data-testid="share-target-copy"
          >
            <Copy size={20} />
            <span className="text-[11px] font-black">Copy</span>
          </button>
        </div>
      </div>
    </div>
  );
}
